import { BaseApi } from '@/apis/base.api';
import { resolveDefectPanelContext } from '@/utils/map/resolveDefectPanelContext';

function extractData(response) {
  return response?.data?.content ?? response?.data ?? response;
}

function halHrefToApiPath(href) {
  if (!href || typeof href !== 'string') {
    return null;
  }
  const match = href.match(/\/api(\/.*)$/);
  return match ? match[1] : href;
}

function isHalLink(value) {
  return (
    value &&
    typeof value === 'object' &&
    typeof value.href === 'string' &&
    value.moduleId == null &&
    value.stringId == null
  );
}

async function fetchHalEntity(hal) {
  const path = halHrefToApiPath(hal?.href);
  if (!path) {
    return null;
  }
  try {
    const response = await BaseApi.get(path);
    return extractData(response);
  } catch (error) {
    console.warn('[DEFECT SESSION] HAL follow 실패:', path, error);
    return null;
  }
}

async function expandSolarModuleGraph(solarModule) {
  if (!solarModule || typeof solarModule !== 'object') {
    return solarModule;
  }

  let expanded = { ...solarModule };

  if (isHalLink(expanded)) {
    const fetched = await fetchHalEntity(expanded);
    if (!fetched) {
      return solarModule;
    }
    expanded = fetched;
  }

  if (expanded.solarString && isHalLink(expanded.solarString)) {
    expanded.solarString = await fetchHalEntity(expanded.solarString);
  }

  if (expanded.solarString?.solarArray && isHalLink(expanded.solarString.solarArray)) {
    expanded.solarString = {
      ...expanded.solarString,
      solarArray: await fetchHalEntity(expanded.solarString.solarArray)
    };
  }

  return expanded;
}

async function fetchSolarModuleBySeq(seq) {
  if (seq == null || seq === '') {
    return null;
  }
  try {
    const response = await BaseApi.get(`/solarModules/${seq}`);
    return extractData(response);
  } catch (error) {
    console.warn('[DEFECT SESSION] solarModules 조회 실패:', seq, error);
    return null;
  }
}

/**
 * 결함 상세에 Array/String/Module ID 해석에 필요한 solarModule 그래프를 보강한다.
 */
export async function enrichDefectSolarModuleDetail(defect) {
  if (!defect || typeof defect !== 'object') {
    return defect;
  }

  let merged = { ...defect };
  let solarModule = merged.solarModule;

  if (typeof solarModule === 'object') {
    solarModule = await expandSolarModuleGraph(solarModule);
    merged = { ...merged, solarModule };
  }

  let context = resolveDefectPanelContext({ defect: merged, solarModule });

  const needsFetch =
    context.arrayId == null ||
    context.stringId == null ||
    (context.moduleId == null && context.solarModuleSeq == null);

  const moduleSeq =
    context.solarModuleSeq ??
    merged.solarModuleSeq ??
    (typeof solarModule === 'object' ? solarModule?.id ?? solarModule?.seq : null);

  if (needsFetch && moduleSeq != null) {
    const fetched = await fetchSolarModuleBySeq(moduleSeq);
    if (fetched) {
      const expanded = await expandSolarModuleGraph(fetched);
      merged = {
        ...merged,
        solarModule: expanded,
        solarModuleSeq: moduleSeq
      };
      context = resolveDefectPanelContext({ defect: merged, solarModule: expanded });
    }
  }

  if (moduleSeq != null && merged.solarModuleSeq == null) {
    merged.solarModuleSeq = moduleSeq;
  }

  return merged;
}
